import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseButton from "@reusables/BaseButton";
import { z } from "zod";
import { refinements } from "@/helpers/refinements";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseInput from "@reusables/BaseInput";
import { useGetIndustriesQuery } from "@redux/features/industries/industriesApi";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { Stack, Typography } from "@mui/material";
import BaseTooltip from "@reusables/BaseTooltip";
import { AlertCircle } from "lucide-react";
import { useGetCountriesQuery, useGetCurrenciesPublicQuery } from "@redux/api/internalApiSlice";
import { getCountryFlag, removeEmpty, toastError } from "@helpers/utils";
import BasePhoneInput from "@reusables/BasePhoneInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { useCreateCompanyMutation } from "@redux/features/companies/companiesApi";
import { toast } from "react-toastify";
import { Company } from "@/types/general";

type CompanyCreationModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreateCompany: (company: Company.Extended) => void;
}

const alphaNumericRefinement = refinements.alphaNumeric;
const domainNameRefinement = refinements.domainName;
const validateCRNRefinement = refinements.validateCRN;
const nonNumericRefinement = refinements.nonNumeric;
const numericRequiredRefinement = refinements.numericRequired;

const schema = z.object({
    name: z.string().trim().min(3).max(255),
    industry: z.object({
        id: z.number(),
        name: z.string()
    }).nullish(),
    phone: z.string().trim(),
    email: z.string().trim().email(),
    /** Optional: RHF may leave value undefined until touched; domain refine allows empty after trim */
    website: z
        .union([z.string(), z.undefined(), z.null()])
        .transform((val) => (typeof val === "string" ? val.trim() : ""))
        .refine(domainNameRefinement.refine, domainNameRefinement.message),
    registration_number: z.string().refine(validateCRNRefinement.refine, validateCRNRefinement.message),
    base_currency: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        symbol: z.string()
    }),
    country: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string()
    }),
    street: z.string().trim().refine(alphaNumericRefinement.refine, alphaNumericRefinement.message),
    street_2: z.string().trim().refine(alphaNumericRefinement.refine, alphaNumericRefinement.message).nullable(),
    city: z.string().trim().refine(nonNumericRefinement.refine, nonNumericRefinement.message),
    zipcode: z.string().trim().refine(numericRequiredRefinement.refine, numericRequiredRefinement.message)
});

type CompanyCreationModalFormTyping = z.infer<typeof schema>

export default function CompanyCreationModal(props: CompanyCreationModalProps) {
    const { t } = useTranslation("", { keyPrefix: "dashboard.companySwitcher.creationModal" });

    const { data: industryOptions, isLoading: industryOptionsLoading } = useGetIndustriesQuery();
    const { data: countryOptions = [], isLoading: countryOptionsLoading } = useGetCountriesQuery();

    const { data: currenciesOptions, isLoading: isCurrenciesOptionsLoading } = useGetCurrenciesPublicQuery();

    const { control, handleSubmit, ...form } = useForm<CompanyCreationModalFormTyping>({
        resolver: zodResolver(schema)
    });

    const [createCompany, { isLoading: isCreateCompanyLoading }] = useCreateCompanyMutation();

    const onSubmit = handleSubmit((data) => {
        createCompany(
            removeEmpty({
                ...data,
                currency: data.base_currency?.id as number,
                industry: data.industry?.id,
                country: data.country?.id as number
            })
        ).unwrap()
            .then((data) => {
                toast.success(t("responses.success"));
                props.onCreateCompany(data);
            })
            .catch(toastError);
    }, console.error);

    useEffect(() => {
        if (!props.isOpen) {
            form.reset();
        }
    }, [form.reset, props.isOpen]);

    return (
        <BaseModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            width={968}
            padding="56px"
        >
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent">{t("heading")}</div>
                <BaseInputsGrid cols={2}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.name.label") + " *"}
                                placeholder={t("fields.name.placeholder")}
                            />
                        )}
                    />

                    <Controller
                        name={"registration_number"}
                        control={control}
                        render={({ field, fieldState }) => (
                            <div>
                                <Stack
                                    direction="row"
                                    alignItems={"center"}
                                    gap={1}
                                    className="mb-2"
                                >
                                    <span
                                        className="font-semibold text-inputs-label-dim">{t("fields.registrationNumber.label")} *</span>

                                    <BaseTooltip
                                        title={<Trans t={t} i18nKey={"fields.registrationNumber.validationRule"}
                                                      components={{ b: <b className={"underline"} /> }} />}>
                                        <div
                                            className="bg-[#D9D6DA] w-[18px] h-[18px] rounded-full flex justify-center items-center transition-all hover:bg-lightGreen-400 cursor-pointer">
                                            <AlertCircle size={12} color={"#686868"} />
                                        </div>
                                    </BaseTooltip>
                                </Stack>
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                />
                            </div>
                        )}
                    />

                    {/* Industry (optional) */}
                    <Controller
                        name={"industry"}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseDropdown
                                {...field}
                                error={fieldState.error}
                                label={t("fields.industry.label")}
                                placeholder={`${t("fields.industry.placeholder")}`}

                                options={industryOptions}
                                isLoading={industryOptionsLoading}

                                getter={{
                                    key: (opt) => opt.id,
                                    label: (opt) => opt.name
                                }}

                                autocomplete
                            />
                        )}
                    />

                    {/* Base Currency (required) */}
                    <div>
                        <Controller
                            name={"base_currency"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    {...fieldState}
                                    label={t("fields.currency.label") + " *"}
                                    options={currenciesOptions}
                                    getter={{
                                        label: opt => opt.name,
                                        key: opt => opt.id,
                                        caption: opt => opt.code
                                    }}
                                    isLoading={isCurrenciesOptionsLoading}
                                    autocomplete
                                />
                            )}
                        />
                        <Typography variant="caption" display="block" sx={{ opacity: 0.5 }}>
                            {t("fields.currency.caption")}
                        </Typography>
                    </div>
                </BaseInputsGrid>

                <BaseInputsGrid cols={2} title={t("sections.address")}>
                    {/* Street */}
                    <Controller
                        name="street"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.street.label") + " *"}
                                placeholder={t("fields.street.placeholder")}
                            />
                        )}
                    />

                    {/* Street 2 (optional) */}
                    <Controller
                        name="street_2"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.street_2.label")}
                                placeholder={t("fields.street_2.placeholder")}
                            />
                        )}
                    />
                </BaseInputsGrid>

                <BaseInputsGrid>
                    {/* City */}
                    <Controller
                        name="city"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.city.label") + " *"}
                                placeholder={t("fields.city.placeholder")}
                            />
                        )}
                    />

                    {/* Zipcode */}
                    <Controller
                        name="zipcode"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.zipcode.label") + " *"}
                                placeholder={t("fields.zipcode.placeholder")}
                            />
                        )}
                    />

                    {/* Country (required) */}
                    <Controller
                        name={"country"}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseDropdown
                                {...field}
                                {...fieldState}
                                label={t("fields.country.label") + " *"}
                                placeholder={t("fields.country.placeholder")}
                                options={countryOptions}
                                getter={{
                                    label: (opt) => opt.name,
                                    key: (opt) => opt.id,
                                    renderOption: (opt, icon) => (
                                        <div>
                                            <img src={getCountryFlag(opt.code)} width={19} alt={opt.code} />
                                            <span className="ml-[7px] grow">{opt.name}</span>
                                            {icon}
                                        </div>
                                    )
                                }}

                                autocomplete
                                isLoading={countryOptionsLoading}
                            />
                        )}
                    />

                    <PhoneInput control={control} />

                    <Controller
                        name={"email"}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.email.label") + " *"}
                            />
                        )}
                    />

                    <Controller
                        name={"website"}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.website.label")}
                            />
                        )}
                    />
                </BaseInputsGrid>

                <div className="gap-[16px]">
                    <BaseButton
                        text={t("buttons.create")}
                        size="md"
                        buttonWidth={"100%"}
                        loading={isCreateCompanyLoading}
                    />
                </div>
            </form>
        </BaseModal>
    );
}

function PhoneInput({ control }: { control: Control<CompanyCreationModalFormTyping> }) {
    const { t } = useTranslation("", { keyPrefix: "dashboard.companySwitcher.creationModal" });

    const selectedCountry = useWatch({
        control,
        name: "country"
    });

    return <Controller
        name={"phone"}
        control={control}
        render={({ field, fieldState }) => (
            <BasePhoneInput
                {...field}
                error={fieldState.error}
                label={t("fields.phone.label") + " *"}

                variant="custom"
                size="md"
                country={selectedCountry?.code}
            />
        )}
    />;
}